import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Stack,
} from '@mui/material';
import heroImg from './assets/ptb_hero_image.png';
import dailyPicksImg from './assets/daily-picks-screen.webp';
import heroCharacterImg from './assets/hero-character.webp';
import { detectPlatform } from './utils/platformDetection';
import { TESTFLIGHT_URL, playStoreUrl, trackStoreClick } from './utils/appStoreLinks';
import { MAIN_LINKS } from './config/siteLinks';
import HeartBalloonLogo from './components/HeartBalloonLogo';
import StoreButtons from './components/StoreButtons';

const LIGHT_PURPLE = '#A76FFF';
const DEEP_PURPLE = '#6B3FD9';
const VIBRANT_RED = '#FF5A76';

const FOOTER_LINKS: Array<[string, string]> = [...MAIN_LINKS, ['/about-us', 'About']];
const LEGAL_LINKS: Array<[string, string]> = [
    ['/privacy-policy', 'Privacy Policy'],
    ['/terms-of-service', 'Terms of Service'],
    ['/cookie-policy', 'Cookies'],
    ['/child-safety', 'Child Safety'],
    ['mailto:hello@poptheballoonapp.com', 'Contact'],
];

// True while the element is on screen
const useInView = (ref: React.RefObject<HTMLElement>) => {
    const [inView, setInView] = useState(true);

    useEffect(() => {
        const element = ref.current;
        if (!element || !('IntersectionObserver' in window)) return;
        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);

    return inView;
};

/** Phones only: keeps a download button in reach once the hero button has scrolled away. */
const StickyGetApp: React.FC<{ platform: 'ios' | 'android'; visible: boolean }> = ({ platform, visible }) => {
    const isAndroid = platform === 'android';

    return (
        <Box
            aria-hidden={!visible}
            sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                pt: 1.25,
                pb: 'calc(10px + env(safe-area-inset-bottom))',
                bgcolor: '#fff',
                borderTop: '1px solid #EDE3FF',
                boxShadow: '0 -6px 20px rgba(0,0,0,0.08)',
                transform: visible ? 'translateY(0)' : 'translateY(110%)',
                transition: 'transform 0.25s ease'
            }}
        >
            <HeartBalloonLogo size={32} color={LIGHT_PURPLE} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#333', lineHeight: 1.3 }}>
                    Pop The Balloon
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#777', lineHeight: 1.3 }}>
                    {isAndroid ? 'Free on Google Play' : 'Free iOS beta'}
                </Typography>
            </Box>
            <Box
                component="a"
                href={isAndroid ? playStoreUrl('sticky') : TESTFLIGHT_URL}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={visible ? 0 : -1}
                onClick={() => trackStoreClick(isAndroid ? 'google_play' : 'ios_beta', 'sticky', platform)}
                sx={{
                    bgcolor: LIGHT_PURPLE,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    px: 2.5,
                    py: 1.25,
                    borderRadius: 999,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap'
                }}
            >
                {isAndroid ? 'Get the app' : 'Join the beta'}
            </Box>
        </Box>
    );
};

const MobileRedirectPage: React.FC = () => {
    const { platform } = detectPlatform();
    const isPhone = platform !== 'desktop';

    const heroCtaRef = useRef<HTMLDivElement>(null);
    const bottomCtaRef = useRef<HTMLDivElement>(null);
    const heroCtaInView = useInView(heroCtaRef);
    const bottomCtaInView = useInView(bottomCtaRef);

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #FCFCFF 0%, #F1E4FF 100%)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            margin: 0,
            padding: 0
        }}>
            {/* Header */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                py: 2,
                px: { xs: 2, md: 4 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HeartBalloonLogo size={40} color="#fff" />
                    <Typography sx={{
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        color: '#fff',
                        textShadow: '0 1px 4px rgba(0,0,0,0.3)'
                    }}>
                        Pop The Balloon
                    </Typography>
                </Box>
                <Box component="nav" aria-label="Main" sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
                    {MAIN_LINKS.map(([href, label]) => (
                        <Box
                            key={href}
                            component="a"
                            href={href}
                            sx={{
                                color: '#fff',
                                textDecoration: 'none',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                '&:hover': { textDecoration: 'underline', textUnderlineOffset: '4px' }
                            }}
                        >
                            {label}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Hero: on phones the balloon image sits above the text so the store button fits on the first screen */}
            <Box component="section" sx={{
                position: 'relative',
                overflow: 'hidden',
                // The gradient starts where the image ends, so there's no seam under the balloon
                background: {
                    xs: `linear-gradient(180deg, ${LIGHT_PURPLE} 260px, ${DEEP_PURPLE} 100%)`,
                    sm: `linear-gradient(180deg, ${LIGHT_PURPLE} 320px, ${DEEP_PURPLE} 100%)`,
                    md: LIGHT_PURPLE
                },
                height: { md: '80vh' },
                minHeight: { md: '640px' },
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
            }}>
                <Box
                    component="img"
                    src={heroImg}
                    alt=""
                    width={1920}
                    height={1800}
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        position: 'absolute',
                        inset: 0
                    }}
                />
                {/* Phones: the ad character above the text, kept to the right so it clears the logo,
                    and short enough that the store button stays on the first screen */}
                <Box sx={{
                    display: { xs: 'flex', md: 'none' },
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    height: { xs: '260px', sm: '320px' },
                    pr: { xs: 2, sm: '10%' }
                }}>
                    <Box sx={{
                        width: { xs: '230px', sm: '290px' },
                        height: { xs: '230px', sm: '290px' },
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(252,250,255,0.95) 0%, rgba(241,228,255,0.85) 55%, rgba(241,228,255,0) 72%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Box
                            component="img"
                            src={heroCharacterImg}
                            alt=""
                            width={536}
                            height={983}
                            sx={{ height: { xs: '250px', sm: '310px' }, width: 'auto' }}
                        />
                    </Box>
                </Box>
                {/* Darkens the left side behind the text on wide screens */}
                <Box sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(90deg, rgba(76,40,170,0.55) 0%, rgba(76,40,170,0) 60%)'
                }} />

                <Box sx={{
                    position: { md: 'absolute' },
                    left: 0,
                    bottom: { md: '12%' },
                    zIndex: 10,
                    maxWidth: { md: '640px' },
                    px: { xs: 3, md: 8 },
                    pt: { xs: 1, md: 0 },
                    pb: { xs: 5, md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                }}>
                    <Typography
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            color: '#fff',
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            fontSize: { xs: '2.25rem', md: '3.5rem' },
                            textShadow: '0 2px 12px rgba(0,0,0,0.25)',
                            animation: 'slideUpFade 0.8s ease-out',
                            '@keyframes slideUpFade': {
                                '0%': { opacity: 0, transform: 'translateY(30px)' },
                                '100%': { opacity: 1, transform: 'translateY(0)' }
                            }
                        }}
                    >
                        Dating without the endless swiping
                    </Typography>
                    <Typography
                        sx={{
                            mt: 2,
                            mb: 3,
                            color: '#fff',
                            fontSize: { xs: '1.05rem', md: '1.25rem' },
                            fontWeight: 500,
                            lineHeight: 1.5,
                            textShadow: '0 1px 8px rgba(0,0,0,0.2)',
                            animation: 'slideUpFade 0.8s ease-out 0.2s backwards',
                        }}
                    >
                        A few picks a day. Keep the ones you like, pop the rest. When someone keeps you, they tell you why.
                    </Typography>
                    <Box ref={heroCtaRef} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <StoreButtons placement="hero" platform={platform} onDark showQr />
                    </Box>
                </Box>

                {/* Illustrated character from the Mauritius ads, on a light glow so the purple
                    balloon stands out (wide screens, where it doesn't cover the text) */}
                <Box sx={{
                    position: 'absolute',
                    right: { lg: '4%', xl: '6%' },
                    top: '50%',
                    transform: 'translateY(-46%)',
                    width: { lg: '420px', xl: '480px' },
                    height: { lg: '420px', xl: '480px' },
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(252,250,255,0.95) 0%, rgba(241,228,255,0.85) 55%, rgba(241,228,255,0) 72%)',
                    display: { xs: 'none', lg: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 5
                }}>
                    <Box
                        component="img"
                        src={heroCharacterImg}
                        alt=""
                        width={536}
                        height={983}
                        sx={{
                            height: { lg: '490px', xl: '560px' },
                            width: 'auto',
                            animation: 'fadeInSlideUp 1.2s cubic-bezier(0.4, 0, 0.2, 1) backwards, floatCharacter 5s ease-in-out 1.2s infinite',
                            '@keyframes fadeInSlideUp': {
                                '0%': { opacity: 0, transform: 'translateY(60px)' },
                                '100%': { opacity: 1, transform: 'translateY(0)' }
                            },
                            '@keyframes floatCharacter': {
                                '0%, 100%': { transform: 'translateY(0)' },
                                '50%': { transform: 'translateY(-14px)' }
                            }
                        }}
                    />
                </Box>
            </Box>

            <Container maxWidth="lg" sx={{ flexGrow: 1, py: 2, px: { xs: 2, sm: 3 } }}>
                {/* How It Works Section */}
                <Box sx={{ mb: 8, py: 6 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                            <Box sx={{ px: { xs: 2, md: 0 } }}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    sx={{ mb: 3, fontWeight: 'bold', color: '#333' }}
                                >
                                    How We Do It
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{ mb: 4, fontWeight: 500, color: 'text.secondary', lineHeight: 1.4 }}
                                >
                                    Born from the hit dating show Pop the Balloon or Find Love, our app lets you experience that same excitement for yourself.
                                </Typography>
                                <Stack spacing={3}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ width: 8, minWidth: 8, bgcolor: LIGHT_PURPLE, borderRadius: 1, mt: 1 }} />
                                        <Typography variant="h6" sx={{ color: 'text.secondary', lineHeight: 1.6, fontWeight: 400 }}>
                                            Each balloon represents a potential connection.
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ width: 8, minWidth: 8, bgcolor: VIBRANT_RED, borderRadius: 1, mt: 1 }} />
                                        <Typography variant="h6" sx={{ color: 'text.secondary', lineHeight: 1.6, fontWeight: 400 }}>
                                            Will you pop to the next single person, or keep it to show your interest?
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ width: 8, minWidth: 8, bgcolor: LIGHT_PURPLE, borderRadius: 1, mt: 1 }} />
                                        <Typography variant="h6" sx={{ color: 'text.secondary', lineHeight: 1.6, fontWeight: 400 }}>
                                            Every choice leads to a real reaction: no filters, no scripts, just genuine chemistry.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Box
                                    component="img"
                                    src={dailyPicksImg}
                                    alt="The Daily Picks screen: pop or keep each pick"
                                    loading="lazy"
                                    width={600}
                                    height={1299}
                                    sx={{
                                        width: '100%',
                                        maxWidth: { xs: '240px', sm: '260px', md: '300px' },
                                        height: 'auto',
                                        borderRadius: '24px',
                                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* CTA Section */}
                <Box
                    ref={bottomCtaRef}
                    sx={{
                        textAlign: 'center',
                        py: 6,
                        px: 2,
                        mb: 4,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, rgba(167, 111, 255, 0.1), rgba(255, 90, 118, 0.1))`,
                        border: `1px solid rgba(167, 111, 255, 0.2)`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                        Ready to Find Your Match?
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: '500px', mx: 'auto' }}>
                        Join thousands of singles already finding genuine connections on the mobile app.
                    </Typography>
                    <StoreButtons placement="bottom" platform={platform} />
                    <Typography sx={{ mt: 3, fontSize: '0.9rem', color: 'text.secondary', maxWidth: '420px' }}>
                        Used Pop the Balloon on the web? It now lives in the app: sign in with the same details.
                    </Typography>
                </Box>
            </Container>

            {/* Footer */}
            <Box sx={{
                bgcolor: '#1a1a2e',
                color: 'rgba(255,255,255,0.7)',
                pt: 4,
                // Room for the sticky download bar on phones
                pb: isPhone ? 'calc(96px + env(safe-area-inset-bottom))' : 4,
                px: { xs: 2, md: 4 },
                textAlign: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <HeartBalloonLogo size={28} color="#A76FFF" />
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>
                        Pop The Balloon
                    </Typography>
                </Box>
                <Stack
                    component="nav"
                    aria-label="Footer"
                    direction="row"
                    spacing={3}
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent="center"
                    sx={{ mb: 1.5 }}
                >
                    {FOOTER_LINKS.map(([href, label]) => (
                        <Box key={href} component="a" href={href} sx={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 600, '&:hover': { color: '#fff' } }}>
                            {label}
                        </Box>
                    ))}
                </Stack>
                <Stack
                    direction="row"
                    spacing={2}
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent="center"
                    sx={{ mb: 2 }}
                >
                    {LEGAL_LINKS.map(([href, label]) => (
                        <Box key={href} component="a" href={href} sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '0.8rem', '&:hover': { color: '#fff' } }}>
                            {label}
                        </Box>
                    ))}
                </Stack>
                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                    &copy; {new Date().getFullYear()} Pop The Balloon. All rights reserved.
                </Typography>
            </Box>

            {platform !== 'desktop' && (
                <StickyGetApp platform={platform} visible={!heroCtaInView && !bottomCtaInView} />
            )}
        </Box>
    );
};

export default MobileRedirectPage;
